import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const CONFIRMATION_HIDE_DELAY = 230;
const DEFAULT_CONFIRMATION_DURATION = 5500;
const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 *
 * Successful add-to-cart events deliberately do not open the drawer. Existing
 * section hydration and cart-count updates continue through the event promise.
 * This component only maintains sticky summary state and the installments CTA.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired.
    if (this.#themeDrawer?.hasAttribute('open')) this.#handleDrawerOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /** Handles opening: update sticky state and wire the installments CTA. */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Re-query on every open because cart hydration can replace the payment element.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * Keep the native cart/section promise intact and only refresh sticky state
   * after the existing cart update settles.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const settle = () => requestAnimationFrame(() => this.#updateStickyState());

    if (!event.promise) {
      settle();
      return;
    }

    event.promise
      .then(() => settle())
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries, so query inside the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

/**
 * Small, non-modal add confirmation. It consumes the already resolved
 * CartLinesUpdateEvent; it makes no cart request and never moves focus.
 *
 * @typedef {object} CartAddConfirmationRefs
 * @property {HTMLElement} message - Polite live-region message.
 * @extends {Component<CartAddConfirmationRefs>}
 */
class CartAddConfirmation extends Component {
  requiredRefs = ['message'];

  /** @type {number | undefined} */
  #autoDismissTimer;

  /** @type {number | undefined} */
  #hideTimer;

  /** @type {number | undefined} */
  #showFrame;

  /** @type {number} */
  #addSequence = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener('click', this.#handleClick);
    this.#clearTimers();
  }

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartLinesUpdate = (event) => {
    if (event.action !== 'add' || !event.promise) return;

    const sequence = ++this.#addSequence;
    const sourceTitle =
      event.target instanceof Element
        ? this.#cleanTitle(event.target.closest('[data-product-title]')?.getAttribute('data-product-title'))
        : '';
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog[open]') : null
    );

    event.promise
      .then((result) => {
        const detail = result?.detail;
        if (sequence !== this.#addSequence || detail?.didError) return;

        const productTitle =
          sourceTitle || this.#cleanTitle(detail?.productTitle) || this.#titleFromResult(event, detail);
        if (!productTitle) return;

        const reveal = () => {
          if (sequence === this.#addSequence) this.#show(productTitle);
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', reveal, { once: true });
        } else {
          reveal();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-confirmation] Add event rejected:', error);
      });
  };

  /** @param {Event} event */
  #handleDrawerOpen = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('theme-drawer#cart-drawer')) return;
    this.#addSequence += 1;
    this.#dismiss(true);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('[data-cart-confirmation-action]');
    if (!(control instanceof HTMLElement) || !this.contains(control)) return;

    const action = control.dataset.cartConfirmationAction;
    if (action === 'view-cart') {
      this.#addSequence += 1;
      this.#dismiss(true);
      requestAnimationFrame(() => {
        const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (
          document.querySelector('theme-drawer#cart-drawer')
        );
        if (typeof drawer?.open === 'function') {
          drawer.open();
        } else {
          window.location.href = (typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart';
        }
      });
      return;
    }

    if (action === 'continue' || action === 'close') {
      this.#addSequence += 1;
      this.#dismiss();
    }
  };

  /** @param {string} productTitle */
  #show(productTitle) {
    this.#clearTimers();

    const template = this.dataset.messageTemplate || '__PRODUCT_NAME__';
    const message = template.replace('__PRODUCT_NAME__', productTitle);
    this.classList.remove('is-visible');
    this.refs.message.textContent = '';
    this.hidden = false;

    this.#showFrame = requestAnimationFrame(() => {
      this.#showFrame = undefined;
      this.refs.message.textContent = message;
      this.classList.add('is-visible');
    });

    const configuredDuration = Number(this.dataset.duration);
    const duration =
      Number.isFinite(configuredDuration) && configuredDuration > 0
        ? configuredDuration
        : DEFAULT_CONFIRMATION_DURATION;
    this.#autoDismissTimer = window.setTimeout(() => this.#dismiss(), duration);
  }

  /** @param {boolean} [immediate] */
  #dismiss(immediate = false) {
    if (this.#autoDismissTimer !== undefined) {
      window.clearTimeout(this.#autoDismissTimer);
      this.#autoDismissTimer = undefined;
    }
    if (this.#showFrame !== undefined) {
      cancelAnimationFrame(this.#showFrame);
      this.#showFrame = undefined;
    }
    if (this.hidden) return;

    this.classList.remove('is-visible');
    if (this.#hideTimer !== undefined) window.clearTimeout(this.#hideTimer);

    if (immediate || REDUCED_MOTION_QUERY.matches) {
      this.hidden = true;
      this.#hideTimer = undefined;
      return;
    }

    this.#hideTimer = window.setTimeout(() => {
      this.hidden = true;
      this.#hideTimer = undefined;
    }, CONFIRMATION_HIDE_DELAY);
  }

  #clearTimers() {
    if (this.#autoDismissTimer !== undefined) window.clearTimeout(this.#autoDismissTimer);
    if (this.#hideTimer !== undefined) window.clearTimeout(this.#hideTimer);
    if (this.#showFrame !== undefined) cancelAnimationFrame(this.#showFrame);
    this.#autoDismissTimer = undefined;
    this.#hideTimer = undefined;
    this.#showFrame = undefined;
  }

  /** @param {unknown} value */
  #cleanTitle(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  /**
   * Resolve the title from cart items already returned with the successful event.
   * No second request and no DOM-wide title guessing.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   * @param {Record<string, any> | undefined} detail
   */
  #titleFromResult(event, detail) {
    const firstLine = /** @type {{ merchandiseId?: string | number, id?: string | number } | undefined} */ (
      Array.isArray(event.lines) ? event.lines[0] : undefined
    );
    const merchandiseId = String(firstLine?.merchandiseId || firstLine?.id || '').split('/').pop();
    if (!merchandiseId || !Array.isArray(detail?.items)) return '';

    const item = detail.items.find((candidate) => {
      const candidateId = candidate?.variant_id ?? candidate?.variantId ?? candidate?.id;
      return String(candidateId || '').split('/').pop() === merchandiseId;
    });

    return this.#cleanTitle(item?.product_title || item?.productTitle || item?.title);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

if (!customElements.get('cart-add-confirmation')) {
  customElements.define('cart-add-confirmation', CartAddConfirmation);
}
